import { useState, useEffect, type FormEvent } from 'react';
import { useCreateTimeEntry, useUpdateTimeEntry, useResolveRate } from '@/hooks/useApi';
import type { TimeEntry, TimeEntryRefType } from '@jerico/shared';

interface TimeEntryModalProps {
  matterId: string;
  clientId: string;
  entry?: TimeEntry | null;
  referenceId?: string;
  referenceType?: TimeEntryRefType;
  referenceLabel?: string;
  prefillDurationMinutes?: number;
  onClose: () => void;
}

export function TimeEntryModal({ matterId, clientId, entry, referenceId, referenceType, referenceLabel, prefillDurationMinutes, onClose }: TimeEntryModalProps) {
  const isEdit = !!entry;
  const createTimeEntry = useCreateTimeEntry(matterId);
  const updateTimeEntry = useUpdateTimeEntry(matterId, entry?.timeEntryId ?? '');

  const [date, setDate] = useState(entry?.date ?? new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState(entry?.description ?? '');
  const [hours, setHours] = useState(
    entry
      ? (entry.durationMinutes / 60).toString()
      : prefillDurationMinutes
        ? (prefillDurationMinutes / 60).toFixed(1)
        : '',
  );
  const [hourlyRate, setHourlyRate] = useState(
    entry ? (entry.hourlyRateCents / 100).toString() : localStorage.getItem('jerico_last_hourly_rate') ?? '',
  );
  const [billable, setBillable] = useState(entry?.billable ?? true);

  const resolveRateQuery = useResolveRate(matterId);
  const [rateSource, setRateSource] = useState<string | null>(null);
  const [rateAutoFilled, setRateAutoFilled] = useState(false);

  // Auto-populate rate from rate card when creating a new entry
  useEffect(() => {
    if (!isEdit && !rateAutoFilled && resolveRateQuery.data?.rateCents && !hourlyRate) {
      setHourlyRate((resolveRateQuery.data.rateCents / 100).toString());
      setRateSource(resolveRateQuery.data.source);
      setRateAutoFilled(true);
    }
  }, [isEdit, rateAutoFilled, resolveRateQuery.data, hourlyRate]);

  const durationMinutes = Math.round(parseFloat(hours || '0') * 60);
  const hourlyRateCents = Math.round(parseFloat(hourlyRate || '0') * 100);
  const amount = (durationMinutes / 60) * (hourlyRateCents / 100);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (hourlyRate) {
      localStorage.setItem('jerico_last_hourly_rate', hourlyRate);
    }

    if (isEdit) {
      await updateTimeEntry.mutateAsync({
        description,
        durationMinutes,
        hourlyRateCents,
        date,
        billable,
      });
    } else {
      await createTimeEntry.mutateAsync({
        description,
        durationMinutes,
        hourlyRateCents,
        date,
        billable,
        clientId,
        referenceId,
        referenceType,
        referenceLabel,
      });
    }
    onClose();
  }

  const isPending = createTimeEntry.isPending || updateTimeEntry.isPending;
  const refLabel = entry?.referenceLabel ?? referenceLabel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Edit Time Entry' : 'Log Time'}
          </h2>
          {refLabel && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {referenceType?.toLowerCase() ?? entry?.referenceType?.toLowerCase()}: {refLabel}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Date</label>
            <input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What did you work on?"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Hours</label>
              <input
                type="number"
                required
                min="0.1"
                step="0.1"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g. 1.5"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Hourly Rate ($)</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="e.g. 350"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              {rateSource && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  From {rateSource.replace('-', ' ')} rate
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Billable
            </label>
            {amount > 0 && (
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Amount: ${amount.toFixed(2)}
              </span>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : isEdit ? 'Update' : 'Log Time'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
